import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: {
          name?: string;
          email?: string;
          customAnswers?: Record<string, string>;
        };
        utm?: {
          utmSource?: string;
          utmMedium?: string;
          utmCampaign?: string;
        };
      }) => void;
    };
  }
}

interface CalendlyEmbedProps {
  url: string;
  prefill?: {
    name?: string;
    email?: string;
    phone?: string;
    company?: string;
  };
  utm?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  };
  className?: string;
  minHeight?: number;
}

/**
 * Calendly inline embed component.
 * Pre-fills contact information and UTM parameters.
 */
export function CalendlyEmbed({
  url,
  prefill,
  utm,
  className = '',
  minHeight = 650,
}: CalendlyEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in strict mode
    if (initializedRef.current) return;

    const initCalendly = () => {
      if (!containerRef.current || !window.Calendly) return;

      initializedRef.current = true;

      // Clear any existing content
      containerRef.current.innerHTML = '';

      window.Calendly.initInlineWidget({
        url,
        parentElement: containerRef.current,
        prefill: prefill ? {
          name: prefill.name,
          email: prefill.email,
          customAnswers: {
            // Map phone and company to Calendly custom questions if configured
            a1: prefill.phone || '',
            a2: prefill.company || '',
          },
        } : undefined,
        utm: utm ? {
          utmSource: utm.utmSource,
          utmMedium: utm.utmMedium,
          utmCampaign: utm.utmCampaign,
        } : undefined,
      });
    };

    // Check if Calendly is already loaded
    if (window.Calendly) {
      initCalendly();
    } else {
      // Wait for Calendly script to load
      const checkCalendly = setInterval(() => {
        if (window.Calendly) {
          clearInterval(checkCalendly);
          initCalendly();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkCalendly), 10000);

      return () => clearInterval(checkCalendly);
    }
  }, [url, prefill, utm]);

  return (
    <div
      ref={containerRef}
      className={`calendly-inline-widget ${className}`}
      data-auto-load="false"
      style={{
        minWidth: '320px',
        width: '100%',
        height: `${minHeight}px`,
      }}
    />
  );
}

/**
 * Default Calendly URL for demo bookings.
 * Configure in environment variable or replace with your Calendly link.
 */
export const CALENDLY_DEMO_URL = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/omniforge/demo';
